<template>
  <section>
    <h2 class="league">
      <span class="league__logo" :style="{ 'backgroundImage': `url(${league.imageUrl}`}" />
      <span class="league__name">{{ league.name }}</span>
    </h2>

    <div class="mb-4">
      <ranking-display
        :id="league.leagueId"
        :is-loading="! updatedRanking"
        :ranking="updatedRanking"
      />
    </div>

    <ul>
      <li v-for="matchData in allMatches" v-show="isMatchVisible(matchData)" :key="matchData.id">
        <suspense>
          <match-display
            :live-data="matchData"
            :league-name="league.name"
            @result-calculated="(result) => handleResultCalculated(matchData.id, result)"
          />
        </suspense>
      </li>
    </ul>
  </section>
</template>

<script setup>
import MatchDisplay from "@/components/match/MatchDisplay.vue";
import RankingDisplay from "@/components/leagues/RankingDisplay.vue";
import { computed, ref, onMounted, watch } from "vue";
import { useMPG } from "@/use/useMPG";
import { calculateUpdatedRanking } from "@/utils/ranking/rankingCalculator";

/**
 * Props
 */
const props = defineProps({
  league: {
    type: Object,
    required: true,
  },
  showAll: {
    type: Boolean,
    default: true,
  },
});

const { getRanking } = useMPG();
const initialRanking = ref(null);
const calculatedMatchesResults = ref({});

const handleResultCalculated = (matchId, result) => {
  calculatedMatchesResults.value[matchId] = result;
};

const fetchRanking = async () => {
  const divisionId = props.league.divisionId || props.league.leagueId?.replace("mpg_league_", "mpg_division_") || props.league.id?.replace("mpg_league_", "mpg_division_");

  if (divisionId) {
    initialRanking.value = null; // Reset ranking while loading
    try {
      initialRanking.value = await getRanking(divisionId);
    } catch (e) {
      console.error("Failed to fetch ranking", e);
    }
  } else {
    console.warn("Could not determine divisionId for league:", props.league);
  }
};

onMounted(fetchRanking);
watch(() => props.league.id, () => {
  calculatedMatchesResults.value = {};
  fetchRanking();
});

const allMatches = computed(() => {
  return Object.values(props.league.liveState?.liveMatches || []);
});

const isMatchVisible = (matchData) => {
  if (props.showAll) {
    return true;
  }
  const userTeamId = props.league.userTeamId;
  return matchData.home.teamId === userTeamId || matchData.away.teamId === userTeamId;
};

const updatedRanking = computed(() => {
  if (! initialRanking.value) {
    return null;
  }

  const matchesForRecalc = allMatches.value.map(matchData => ({
    home: {
      teamId: matchData.home.teamId,
      score: matchData.home.score,
    },
    away: {
      teamId: matchData.away.teamId,
      score: matchData.away.score,
    },
    recalculated: calculatedMatchesResults.value[matchData.id],
  }));

  return calculateUpdatedRanking(initialRanking.value, matchesForRecalc);
});
</script>

<style lang="scss" scoped>
.league {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;

  &__logo {
    display: block;
    width: 50px;
    height: 50px;
    border-radius: 50%;
    background-size: cover;
  }
}

ul {
  padding: 0;

  li {
    list-style: none;
    margin-bottom: 10px;
  }
}

.ranking-sidebar {
  min-height: 200px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;

  @media (max-width: 991px) {
    margin-top: 2rem;
  }
}

.spinner-border {
  margin-top: 3rem;
  width: 3rem;
  height: 3rem;
}
</style>